/**
 * Doppler Ultrasound Calculations
 * Standard indices for vascular assessment
 *
 * See /docs/OB_CALCULATION_REFERENCES.md for formula sources
 */

// ============================================
// DOPPLER INDICES
// ============================================

/**
 * Pulsatility Index (PI)
 * PI = (PSV - EDV) / TAV
 * Where TAV = Time-Averaged Velocity (mean velocity over cardiac cycle)
 */
export const calculatePI = (psv, edv, tav) => {
  if (!psv || !tav || tav === 0) return null;
  return Math.round(((psv - edv) / tav) * 100) / 100;
};

/**
 * Simplified PI when TAV not available
 * PI = (PSV - EDV) / Mean
 * Mean approximated as (PSV + EDV) / 2 for some applications
 */
export const calculatePI_Simplified = (psv, edv) => {
  if (!psv || psv === 0) return null;
  const edvVal = edv || 0;
  const mean = (psv + edvVal) / 2;
  if (mean === 0) return null;
  return Math.round(((psv - edvVal) / mean) * 100) / 100;
};

/**
 * Resistance Index (RI) - Pourcelot Index
 * RI = (PSV - EDV) / PSV
 */
export const calculateRI = (psv, edv) => {
  if (!psv || psv === 0) return null;
  const edvVal = edv || 0;
  return Math.round(((psv - edvVal) / psv) * 100) / 100;
};

/**
 * Systolic/Diastolic Ratio (S/D)
 * S/D = PSV / EDV
 */
export const calculateSD = (psv, edv) => {
  if (!psv || !edv || edv === 0) return null;
  return Math.round((psv / edv) * 100) / 100;
};

/**
 * Calculate all indices from PSV, EDV, and optionally TAV
 */
export const calculateAllIndices = (psv, edv, tav = null) => {
  return {
    ri: calculateRI(psv, edv),
    pi: tav ? calculatePI(psv, edv, tav) : calculatePI_Simplified(psv, edv),
    sd: calculateSD(psv, edv),
    psv: psv ? Math.round(psv * 10) / 10 : null,
    edv: edv ? Math.round(edv * 10) / 10 : null,
    tav: tav ? Math.round(tav * 10) / 10 : null,
  };
};

// ============================================
// UMBILICAL ARTERY (UA)
// ============================================

/**
 * UA PI reference ranges by gestational age (Acharya et al.)
 * Returns { mean, sd, p5, p95 }
 */
const uaPIReference = {
  20: { mean: 1.23, sd: 0.21 }, 21: { mean: 1.20, sd: 0.20 },
  22: { mean: 1.17, sd: 0.20 }, 23: { mean: 1.14, sd: 0.19 },
  24: { mean: 1.11, sd: 0.19 }, 25: { mean: 1.08, sd: 0.18 },
  26: { mean: 1.05, sd: 0.18 }, 27: { mean: 1.02, sd: 0.17 },
  28: { mean: 0.99, sd: 0.17 }, 29: { mean: 0.96, sd: 0.16 },
  30: { mean: 0.93, sd: 0.16 }, 31: { mean: 0.90, sd: 0.15 },
  32: { mean: 0.87, sd: 0.15 }, 33: { mean: 0.84, sd: 0.14 },
  34: { mean: 0.81, sd: 0.14 }, 35: { mean: 0.78, sd: 0.13 },
  36: { mean: 0.75, sd: 0.13 }, 37: { mean: 0.72, sd: 0.12 },
  38: { mean: 0.69, sd: 0.12 }, 39: { mean: 0.66, sd: 0.11 },
  40: { mean: 0.63, sd: 0.11 },
};

export const interpretUA_PI = (pi, gaWeeks) => {
  if (!pi || !gaWeeks) return null;

  const roundedGA = Math.round(gaWeeks);
  if (roundedGA < 20 || roundedGA > 40) {
    return { status: 'GA out of range', severity: 'unknown' };
  }

  const ref = uaPIReference[roundedGA];
  const p5 = ref.mean - (1.645 * ref.sd);
  const p95 = ref.mean + (1.645 * ref.sd);

  if (pi < p5) {
    return { status: 'Below 5th percentile', severity: 'low', percentile: '<5' };
  }
  if (pi > p95) {
    return { status: 'Above 95th percentile - Increased resistance', severity: 'abnormal', percentile: '>95' };
  }
  return { status: 'Normal', severity: 'normal', percentile: '5-95' };
};

/**
 * Absent or Reversed End Diastolic Flow assessment
 */
export const assessUAFlow = (edv) => {
  if (edv === null || edv === undefined) return null;

  if (edv < 0) {
    return { status: 'Reversed End Diastolic Flow (REDF)', severity: 'critical' };
  }
  if (edv === 0) {
    return { status: 'Absent End Diastolic Flow (AEDF)', severity: 'abnormal' };
  }
  return { status: 'Positive End Diastolic Flow', severity: 'normal' };
};

// ============================================
// MIDDLE CEREBRAL ARTERY (MCA)
// ============================================

/**
 * MCA PSV reference for fetal anemia screening (Mari et al.)
 * Returns MoM (Multiples of Median)
 */
const mcaPSVMedian = {
  // GA: median PSV in cm/s
  18: 23.2, 19: 25.0, 20: 26.9, 21: 28.9, 22: 31.1, 23: 33.4,
  24: 35.9, 25: 38.6, 26: 41.5, 27: 44.5, 28: 47.7, 29: 51.1,
  30: 54.7, 31: 58.5, 32: 62.5, 33: 66.6, 34: 71.0, 35: 75.5,
  36: 80.2, 37: 85.0, 38: 90.0, 39: 95.2, 40: 100.5,
};

export const calculateMCA_MoM = (psvCmS, gaWeeks) => {
  if (!psvCmS || !gaWeeks) return null;

  const roundedGA = Math.round(gaWeeks);
  if (roundedGA < 18 || roundedGA > 40) return null;

  const median = mcaPSVMedian[roundedGA];
  return Math.round((psvCmS / median) * 100) / 100;
};

export const interpretMCA_PSV = (psvCmS, gaWeeks) => {
  const mom = calculateMCA_MoM(psvCmS, gaWeeks);
  if (!mom) return null;

  if (mom >= 1.5) {
    return {
      mom,
      status: 'Elevated - Risk of moderate/severe fetal anemia',
      severity: 'abnormal',
    };
  }
  if (mom >= 1.29) {
    return {
      mom,
      status: 'Borderline elevated',
      severity: 'borderline',
    };
  }
  return {
    mom,
    status: 'Normal',
    severity: 'normal',
  };
};

/**
 * MCA PI reference ranges
 */
const mcaPIReference = {
  20: { mean: 1.83, sd: 0.34 }, 22: { mean: 1.80, sd: 0.33 },
  24: { mean: 1.76, sd: 0.31 }, 26: { mean: 1.72, sd: 0.30 },
  28: { mean: 1.68, sd: 0.28 }, 30: { mean: 1.63, sd: 0.27 },
  32: { mean: 1.58, sd: 0.25 }, 34: { mean: 1.52, sd: 0.24 },
  36: { mean: 1.46, sd: 0.22 }, 38: { mean: 1.39, sd: 0.21 },
  40: { mean: 1.32, sd: 0.19 },
};

export const interpretMCA_PI = (pi, gaWeeks) => {
  if (!pi || !gaWeeks) return null;

  const roundedGA = Math.round(gaWeeks / 2) * 2; // Round to nearest even
  if (roundedGA < 20 || roundedGA > 40) return null;

  const ref = mcaPIReference[roundedGA];
  if (!ref) return null;

  const p5 = ref.mean - (1.645 * ref.sd);

  if (pi < p5) {
    return { status: 'Below 5th percentile - Brain sparing effect', severity: 'abnormal' };
  }
  return { status: 'Normal', severity: 'normal' };
};

// ============================================
// CEREBROPLACENTAL RATIO (CPR)
// ============================================

/**
 * CPR = MCA PI / UA PI
 * Assesses redistribution of blood flow
 */
export const calculateCPR = (mcaPI, uaPI) => {
  if (!mcaPI || !uaPI || uaPI === 0) return null;
  return Math.round((mcaPI / uaPI) * 100) / 100;
};

export const interpretCPR = (cpr, gaWeeks) => {
  if (!cpr) return null;

  // Simplified threshold - more detailed tables available
  const threshold = gaWeeks >= 34 ? 1.08 : 1.0;

  if (cpr < threshold) {
    return {
      status: 'Abnormal - Cerebral redistribution',
      severity: 'abnormal',
      threshold,
    };
  }
  return {
    status: 'Normal',
    severity: 'normal',
    threshold,
  };
};

// ============================================
// UTERINE ARTERY
// ============================================

/**
 * Uterine artery PI interpretation
 */
export const interpretUterinePI = (pi, gaWeeks, hasNotch = false) => {
  if (!pi || !gaWeeks) return null;

  // First trimester (11-14 weeks)
  if (gaWeeks >= 11 && gaWeeks <= 14) {
    if (pi > 2.35) {
      return { status: 'Elevated - Increased preeclampsia risk', severity: 'abnormal' };
    }
    return { status: 'Normal', severity: 'normal' };
  }

  // Second trimester (20-24 weeks)
  if (gaWeeks >= 20 && gaWeeks <= 24) {
    if (pi > 1.45) {
      return {
        status: hasNotch
          ? 'Elevated with notch - High risk'
          : 'Elevated',
        severity: 'abnormal'
      };
    }
    if (hasNotch) {
      return { status: 'Normal PI but notch present', severity: 'borderline' };
    }
    return { status: 'Normal', severity: 'normal' };
  }

  // Third trimester
  if (pi > 1.0) {
    return { status: 'Elevated for gestational age', severity: 'borderline' };
  }
  return { status: 'Normal', severity: 'normal' };
};

// ============================================
// DUCTUS VENOSUS
// ============================================

/**
 * Ductus Venosus PI interpretation (first trimester)
 */
export const interpretDuctusVenosus = (pi, aWavePositive = true) => {
  if (pi === null || pi === undefined) return null;

  if (!aWavePositive) {
    return { status: 'Reversed a-wave - Abnormal', severity: 'abnormal' };
  }

  // First trimester screening (11-14 weeks)
  if (pi > 0.95) {
    return { status: 'Elevated PI', severity: 'borderline' };
  }
  return { status: 'Normal', severity: 'normal' };
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get severity color for UI
 */
export const getSeverityColor = (severity) => {
  switch (severity) {
    case 'normal': return '#4caf50';
    case 'borderline': return '#ff9800';
    case 'abnormal': return '#f44336';
    case 'critical': return '#9c27b0';
    default: return '#9e9e9e';
  }
};

/**
 * Format Doppler report summary
 */
export const formatDopplerSummary = (vessel, indices, interpretation) => {
  if (!indices || !interpretation) return null;

  return {
    vessel,
    ri: indices.ri,
    pi: indices.pi,
    sd: indices.sd,
    psv: indices.psv,
    edv: indices.edv,
    status: interpretation.status,
    severity: interpretation.severity,
  };
};

export default {
  // Core calculations
  calculatePI,
  calculatePI_Simplified,
  calculateRI,
  calculateSD,
  calculateAllIndices,

  // Umbilical Artery
  interpretUA_PI,
  assessUAFlow,

  // Middle Cerebral Artery
  calculateMCA_MoM,
  interpretMCA_PSV,
  interpretMCA_PI,

  // CPR
  calculateCPR,
  interpretCPR,

  // Uterine Artery
  interpretUterinePI,

  // Ductus Venosus
  interpretDuctusVenosus,

  // Utilities
  getSeverityColor,
  formatDopplerSummary,
};
